import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Download, Send, Eye, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DailyReportPDF = ({ sites, user }) => {
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedDate, setSelectedDate] = useState(
    new Date(Date.now() - 86400000).toISOString().split('T')[0] // Yesterday
  );
  const [recipientEmail, setRecipientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const handlePreview = async () => {
    if (!selectedSite) {
      toast.warning('Veuillez sélectionner un site');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const url = `${BACKEND_URL}/api/reports/preview-pdf/${selectedSite}?date=${selectedDate}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `rapport_${selectedDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        toast.success('Rapport PDF téléchargé');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedSite) {
      toast.warning('Veuillez sélectionner un site');
      return;
    }
    if (!recipientEmail) {
      toast.warning('Veuillez entrer un email destinataire');
      return;
    }

    setSending(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${BACKEND_URL}/api/reports/send-daily-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: selectedSite,
          recipient_email: recipientEmail,
          date: selectedDate
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Rapport envoyé à ${recipientEmail} (${data.events_count} événements)`);
        setRecipientEmail('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  // Only admins and chef_poste can send reports
  const canSendReports = user?.role === 'admin' || user?.role === 'chef_poste';

  return (
    <Card className="glass-effect">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-red-600" />
          <CardTitle>Rapport PDF Quotidien</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Site</Label>
            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger data-testid="pdf-site-select">
                <SelectValue placeholder="Sélectionner un site" />
              </SelectTrigger>
              <SelectContent>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Date du rapport</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              data-testid="pdf-date-input"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handlePreview}
            variant="outline"
            className="flex-1"
            disabled={loading || !selectedSite}
            data-testid="pdf-preview-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Télécharger PDF
          </Button>
        </div>

        {canSendReports && (
          <>
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium mb-2 block">Envoyer par email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="destinataire@email.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="flex-1"
                  data-testid="pdf-email-input"
                />
                <Button
                  onClick={handleSendEmail}
                  className="btn-primary"
                  disabled={sending || !selectedSite || !recipientEmail}
                  data-testid="pdf-send-btn"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Envoyer
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Envoyez le rapport PDF directement par email au client ou responsable
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyReportPDF;
